import { useCallback, useMemo, useRef, useState } from 'react';
import { Jodit } from 'jodit-react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { getLocalStorage } from '@shared/core';
import { sanitizeHtml } from '../util';

const CUSTOM_CSS_DIALOG_PREFERENCE_KEY = 'TextComponentPasteWarningSnoozeUntil';
const PREFERENCE_EXPIRY_DAYS = 1;

type UseCustomCssPasteReturn = Readonly<{
  customCssDetectionPlugin: {
    onPaste: (html: string, editor: Jodit) => boolean;
  };
  dialogProps: {
    isOpen: boolean;
    onRemoveFormatting: () => void;
    onKeepFormatting: () => void;
    dontShowDialogAgain: boolean;
    setDontShowDialogAgain: (value: boolean) => void;
  };
}>;

const localStorage = getLocalStorage();

/**
 * Set a preference in localStorage with an expiry timestamp
 * @param key - The localStorage key
 * @param expiryDays - Number of days until the preference expires
 */
const setPreferenceWithExpiry = (key: string, expiryDays: number): void => {
  const expiry = Date.now() + expiryDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds
  localStorage.setItem(key, JSON.stringify(expiry));
};

/**
 * Get a preference from localStorage, checking if it has expired
 * @param key - The localStorage key
 * @returns true if preference exists and not expired, false otherwise
 */
const getPreferenceWithExpiry = (key: string): boolean => {
  const itemStr = localStorage.getItem(key);

  // If no item exists, return false
  if (!itemStr) {
    return false;
  }

  try {
    const expiry = JSON.parse(itemStr);

    // Check if the item has expired
    if (Date.now() > expiry) {
      // Remove expired item by setting it to null
      localStorage.setItem(key, null);
      return false;
    }

    return true;
  } catch {
    // Invalid format, remove it
    localStorage.setItem(key, null);
    return false;
  }
};

export const useCustomCssPaste = (): UseCustomCssPasteReturn => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dontShowDialogAgain, setDontShowDialogAgain] = useState(false);
  const pendingHtmlRef = useRef<string>('');
  const editorRef = useRef<Jodit | null>(null);

  /**
   * Detects if HTML content contains custom CSS styling
   * @param html - The HTML string to check
   * @returns true if custom CSS/styling is detected, false otherwise
   * @security Uses DOMParser to safely parse HTML without executing scripts
   */
  const hasCustomCss = useCallback((html: string): boolean => {
    if (!html) return false;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Check for inline styles
    if (doc.querySelector('[style]')) return true;

    // Check for class attributes
    if (doc.querySelector('[class]')) return true;

    // Check for style tags
    if (doc.querySelector('style')) return true;

    // Check for font tags
    if (doc.querySelector('font')) return true;

    return false;
  }, []);

  const getPlainText = useCallback((html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent?.trim() || '';
  }, []);

  const onPaste = useEventCallback((html: string, editor: Jodit): boolean => {
    if (!editor || !html) return false;

    editorRef.current = editor;

    // Check for custom CSS
    if (hasCustomCss(html)) {
      pendingHtmlRef.current = html;

      // Check user preference with expiry
      const hasPreference = getPreferenceWithExpiry(CUSTOM_CSS_DIALOG_PREFERENCE_KEY);

      if (hasPreference) {
        // User chose "don't show again" - always allow HTML paste (keep formatting)
        editor.s.insertHTML(sanitizeHtml(html));
        return true; // Prevent default paste
      } else {
        // Show dialog
        setIsDialogOpen(true);
        return true; // Prevent default paste
      }
    }

    // No custom CSS, allow normal paste
    return false;
  });

  const handleRemoveFormatting = useEventCallback(() => {
    // Save preference with expiry if checkbox is checked
    // When "don't show again" is checked, future pastes will allow HTML by default
    if (dontShowDialogAgain) {
      setPreferenceWithExpiry(CUSTOM_CSS_DIALOG_PREFERENCE_KEY, PREFERENCE_EXPIRY_DAYS);
    }

    setIsDialogOpen(false);
    setDontShowDialogAgain(false);
    editorRef.current?.async.requestIdleCallback(() => {
      if (pendingHtmlRef.current && editorRef.current) {
        const plainText = getPlainText(pendingHtmlRef.current);
        editorRef.current.s.insertHTML(plainText);
      }
      pendingHtmlRef.current = '';
      editorRef.current = null;
    });
  });

  const handleKeepFormatting = useEventCallback(() => {
    // Save preference with expiry if checkbox is checked
    // When "don't show again" is checked, future pastes will allow HTML by default
    if (dontShowDialogAgain) {
      setPreferenceWithExpiry(CUSTOM_CSS_DIALOG_PREFERENCE_KEY, PREFERENCE_EXPIRY_DAYS);
    }

    setIsDialogOpen(false);
    setDontShowDialogAgain(false);
    editorRef.current?.async.requestIdleCallback(() => {
      if (pendingHtmlRef.current && editorRef.current) {
        editorRef.current.s.insertHTML(sanitizeHtml(pendingHtmlRef.current));
      }
      pendingHtmlRef.current = '';
      editorRef.current = null;
    });
  });

  const dialogProps = useMemo(
    () => ({
      isOpen: isDialogOpen,
      onRemoveFormatting: handleRemoveFormatting,
      onKeepFormatting: handleKeepFormatting,
      dontShowDialogAgain,
      setDontShowDialogAgain,
    }),
    [isDialogOpen, handleRemoveFormatting, handleKeepFormatting, dontShowDialogAgain],
  );

  const customCssDetectionPlugin = useMemo(
    () => ({
      onPaste,
    }),
    [onPaste],
  );

  return {
    customCssDetectionPlugin,
    dialogProps,
  };
};
